import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';

const { FiCheck, FiZap, FiUsers, FiShield, FiTarget, FiCrown } = FiIcons;

const Pricing = () => {
  const { lang } = useLanguage();

  const plan = {
    name: "Stup-On | Platform Access",
    description: lang === 'de'
      ? "Kostenloser Zugang zur 10-Schritte-Methodik für alle registrierten Nutzer."
      : "Free access to the 10-step methodology for all registered users.",
    features: [
      lang === 'de' ? "Alle 10 Schritte freigeschaltet" : "All 10 steps unlocked",
      lang === 'de' ? "Detaillierte Aufgabenstellung" : "Detailed assignment",
      lang === 'de' ? "Zielgruppenanalyse" : "Target group analysis",
      lang === 'de' ? "Angebotsformulierung" : "Offer formulation",
      lang === 'de' ? "Messbare Meilensteine" : "Measurable milestones",
      lang === 'de' ? "Community Support" : "Community Support"
    ]
  };

  const scrollToRegistration = () => {
    const element = document.getElementById('registration-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-blue-50 text-blue-600 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-6 border border-blue-100"
          >
            <SafeIcon icon={FiZap} />
            <span>{lang === 'de' ? 'Zugang' : 'Access'}</span>
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight leading-tight uppercase">
            {lang === 'de' ? 'Starte jetzt kostenlos' : 'Start now for free'}
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-medium">
            {lang === 'de'
              ? 'Erhalte vollen Zugriff auf unsere bewährte Methodik durch eine einfache Registrierung.'
              : 'Get full access to our proven methodology through a simple registration.'
            }
          </p>
        </div>

        {/* Access Card */}
        <div className="flex justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-white rounded-[2.5rem] p-10 md:p-14 shadow-2xl border-2 border-blue-500 ring-4 ring-blue-100 w-full max-w-xl"
          >
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-black uppercase tracking-widest shadow-lg">
                <SafeIcon icon={FiCrown} />
                <span>{lang === 'de' ? 'Vollzugriff' : 'Full Access'}</span>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 bg-blue-100 text-blue-600">
                <SafeIcon icon={FiTarget} className="text-3xl" />
              </div>

              <h3 className="text-2xl font-black text-gray-900 mb-2 uppercase tracking-tight">
                {lang === 'de' ? 'Plattform-Registrierung' : 'Platform Registration'}
              </h3>

              <p className="text-gray-600 font-medium mb-6 leading-relaxed">
                {plan.description}
              </p>

              <div className="mb-2">
                <div className="flex items-baseline justify-center space-x-2">
                  <span className="text-6xl md:text-7xl font-black text-gray-900">
                    FREE
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-10">
              {plan.features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-center space-x-4"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                    <SafeIcon icon={FiCheck} className="text-sm" />
                  </div>
                  <span className="text-gray-700 font-bold">{feature}</span>
                </motion.div>
              ))}
            </div>

            <button
              onClick={scrollToRegistration}
              className="block w-full text-center py-4 px-8 rounded-xl font-black text-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-200 hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300 uppercase tracking-widest"
            >
              {lang === 'de' ? 'Kostenlos starten' : 'Start for free'}
            </button>

            <div className="mt-6 flex items-center justify-center space-x-4 text-sm text-gray-500 font-medium">
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiShield} className="text-green-500" />
                <span>{lang === 'de' ? 'Sicher' : 'Secure'}</span>
              </div>
              <div className="w-1 h-1 bg-gray-300 rounded-full" />
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiUsers} className="text-blue-500" />
                <span>{lang === 'de' ? '500+ Nutzer' : '500+ Users'}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;